// TODO fix this dependency cycle
// eslint-disable-next-line import/no-cycle
import {InternalUser, Product, RecommendedProductsRequest, RecommendedProductsResponse} from '@amplify/athena-models';
import {AllProductType} from '../CaseManager/Forms';
import API from './API';
import {ClientWalkthroughData} from './CRM';
import {
    AssistContent,
    AssistContentItem,
    AssociateSsnWithFormRequest,
    CalcFocusEmailRequest,
    CreateDealApiResponse,
    GetContactsApiResponse,
    GetFormsForContactByEmailApiResponse,
    HubspotProsperDealRequest,
    HubspotProsperDealStages,
    SsnForm,
    SsnFormsRequest,
} from './types';
import type {LeaderboardResponse} from '../Leaderboard/data';

export default class AthenaAPI {
    public static async getSsnForms(request: SsnFormsRequest): Promise<SsnForm[] | null> {
        const response = await API.Call<{data: SsnForm[]}>('v2/identity/ssn-forms', 'POST', request);

        if (response) {
            return response.data;
        }

        return null;
    }

    public static async associateSsnWithForm(request: AssociateSsnWithFormRequest): Promise<void> {
        await API.Call<{data: void}>('v2/identity/associate-ssn-with-form', 'POST', request);
    }

    public static async getAllInternalUsers(): Promise<InternalUser[]> {
        const response = (await API.CallFullResponse<{data: void}>('v2/internal/users', 'GET')) as any;
        if (response?.status === 200 && Array.isArray(response.data)) {
            return response.data as InternalUser[];
        } else {
            throw new Error('Error getting internal users', response.data);
        }
    }

    public static async getInternalUser(email: string): Promise<InternalUser> {
        const response = (await API.CallFullResponse<{data: void}>(`v2/internal/user/${email}`, 'GET')) as any;
        if (response?.status === 200) {
            return response.data as InternalUser;
        } else {
            throw new Error('Error getting internal user', response.data);
        }
    }
    public static async calcfocus(request: any): Promise<any> {
        return await API.Call<{data: void}>('v2/calcfocus/report', 'POST', request);
    }

    public static async getApplicationById(applicationId: string): Promise<any> {
        return await API.Call<{data: void}>(`v2/calcfocus/report/${applicationId}`, 'GET');
    }

    public static async getPolicyByInstantIulApplicationId(instantIulApplicationId: string): Promise<any> {
        return await API.Call<{data: void}>(`v2/calcfocus/policy/${instantIulApplicationId}`, 'GET');
    }

    public static async sendCalcfocusConsentEmail(request: CalcFocusEmailRequest): Promise<any> {
        return await API.Call<{data: void}>('v2/calcfocus/email', 'POST', request);
    }

    public static async getActiveCall(agentEmail: string): Promise<any> {
        return await API.Call<{data: void}>(`v2/live-tab/active-call/${agentEmail}`, 'GET');
    }

    public static async updateInternalUsers(users: InternalUser[]): Promise<void> {
        const response = (await API.CallFullResponse<{data: void}>(`v2/internal/users/batch`, 'PUT', {users})) as any;
        if (!response || response?.status !== 201) {
            throw new Error('Error updating internal users', response.data);
        }
    }

    public static async deleteInternalUser(email: string): Promise<void> {
        const response = (await API.CallFullResponse<{data: void}>(
            `v2/internal/users/${encodeURIComponent(email)}`,
            'DELETE',
        )) as any;
        if (!response || response?.status !== 200) {
            throw new Error('Error deleting internal user', response.body);
        }
    }

    public static async upsertProsperDeal(
        stage: HubspotProsperDealStages,
        customerId: string,
        request?: HubspotProsperDealRequest,
    ) {
        const response = await API.Call<CreateDealApiResponse>(
            `v2/internal/deal/${customerId}/${stage}`,
            'POST',
            request,
        );
        if (response) return response;
        return null;
    }

    public static async getStatesForAgent(email: string): Promise<any> {
        return await API.Call<{data: void}>(`v2/lead-assignment/agentLicensedStates?login=${email}`, 'GET');
    }

    public static async createPreapprovalTask(hubspotContactEmail: string, formLink: string) {
        const response = await API.Call<{data: void; success: boolean}>(`v2/task/preapproval`, 'POST', {
            hubspotContactEmail,
            formLink,
        });
        if (response) return response;
        return null;
    }

    public static async storeQuestionAndAnswer({
        applicationId,
        questionKey,
        answer,
        bootstrapped,
        formId,
    }: {
        applicationId: string;
        questionKey: string;
        answer: string;
        bootstrapped: boolean;
        formId: string;
    }) {
        const resp = await API.Call(`v2/internal/analytics/questions?customerId=${formId}`, 'POST', {
            applicationId,
            questionKey,
            answer,
            bootstrapped,
        });

        return resp;
    }

    public static async storeBulkQuestionAndAnswer({
        applicationId,
        questions,
        formId,
    }: {
        applicationId: string;
        formId: string;
        questions: {questionKey: string; answer: string; bootstrapped: boolean}[];
    }) {
        const resp = await API.Call(`v2/internal/analytics/questions/bulk?customerId=${formId}`, 'POST', {
            applicationId,
            questions,
        });

        return resp;
    }

    public static async storeDecision({
        applicationId,
        decision,
        formId,
    }: {
        applicationId: string;
        decision: string;
        formId: string;
    }) {
        const resp = await API.Call(`v2/internal/analytics/decision?customerId=${formId}`, 'POST', {
            applicationId,
            decision,
        });

        return resp;
    }

    public static async getQualifiedProducts(
        data: ClientWalkthroughData,
    ): Promise<{availableProducts: AllProductType[]; recommendedProductResponse: RecommendedProductsResponse | null}> {
        const recommendedProductsRequest: RecommendedProductsRequest = {
            userData: data?.userData,
            userIdData: data?.userIdData,
            fetchOnly: true,
            includeApOnly: true,
        };

        if (!recommendedProductsRequest.userData || !recommendedProductsRequest.userIdData) {
            return {availableProducts: [], recommendedProductResponse: null};
        }
        const [recommendedProductResponse, allProducts] = await Promise.all([
            API.Call<RecommendedProductsResponse>(
                'v2/recommendation/recommended-products-internal',
                'POST',
                recommendedProductsRequest,
            ),
            API.Call<{products: AllProductType[]}>('v2/product', 'GET').then((r) => r?.products || []),
        ]);

        if (!recommendedProductResponse) {
            throw new Error('Failed to fetch recommended-products');
        }

        const qualifiedProducts = recommendedProductResponse.products
            .map((p) => {
                return {...p, recommended: true};
            })
            .flat(2);

        const qualifiedProductsSet = new Set(qualifiedProducts.map((qp) => qp.code));
        const unqualifiedProducts = allProducts.filter((ap: Product) => !qualifiedProductsSet.has(ap.code));
        const availableProducts = [...qualifiedProducts, ...unqualifiedProducts] as AllProductType[];

        return {availableProducts, recommendedProductResponse};
    }

    public static async getContacts({
        search = '',
        page = 1,
        limit = 10,
    }: {search?: string; page?: number; limit?: number} = {}) {
        return API.Call<GetContactsApiResponse>(`v2/internal/contacts?q=${search}&page=${page}&limit=${limit}`, 'GET');
    }

    public static async getFormsForContactByEmail({email}: {email: string}) {
        return API.Call<GetFormsForContactByEmailApiResponse>(`v2/internal/contacts/${email}/forms`, 'GET');
    }

    public static async getAssistContents(): Promise<AssistContent[]> {
        const response = await API.Call<{data: AssistContent[]}>(`v2/agent-assist`, 'GET');
        return response?.data ?? [];
    }

    public static async getAssistContentsByTab(tabId: string): Promise<AssistContent[]> {
        const response = await API.Call<{data: AssistContent[]}>(`v2/agent-assist/tab/${tabId}`, 'GET');
        return response?.data ?? [];
    }

    public static async getAssistContentsByIds(ids: string[]): Promise<AssistContentItem[]> {
        const response = await API.Call<{data: AssistContentItem[]}>(`v2/agent-assist/content/batch`, 'POST', {ids});
        return response?.data ?? [];
    }

    public static async updateAssistContent(id: string, content: string): Promise<void> {
        await API.Call<{data: void}>(`v2/agent-assist/${id}`, 'PUT', {content});
    }

    public static async createAssistContent(
        triggerId: string,
        type: 'script' | 'faq' | 'tips',
        content: string,
    ): Promise<AssistContentItem> {
        const response = await API.Call<{data: AssistContentItem}>(`v2/agent-assist/${triggerId}/content`, 'POST', {
            type,
            content,
        });
        if (!response?.data) {
            throw new Error('Failed to create assist content');
        }
        return response.data;
    }

    public static async getLeaderboard(): Promise<LeaderboardResponse | null> {
        return API.Call<LeaderboardResponse>('v2/leaderboard', 'GET');
    }

    public static async getHubspotProperties(
        objectType: 'contact' | 'deal',
        objectId: string,
        properties: string[],
    ): Promise<{properties: Record<string, string>} | null> {
        const response = await API.Call<{data: {properties: Record<string, string>}}>(
            `v2/internal/hubspot/${objectType}/${objectId}?properties=${properties.join(',')}`,
            'GET',
        );
        return response?.data ?? null;
    }
}
